import { Divider } from "@fluentui/react-components";
import { AddAttachments } from "components/AddAttachments/AddAttachments";
import CheckboxField from "components/controls/CheckboxField";
import InputField from "components/controls/InputField";
import SelectField from "components/controls/SelectField";
import { TextareaField } from "components/controls/TextareaField";
import { ManagePackageDocuments } from "components/ManagePackageDocuments/ManagePackageDocuments";
import { IPackageRowItem } from "components/PackagesView/components/BenefitImplementationPackages/shared";
import { Grid } from "components/styled";
import { UploadFile } from "components/Upload/UploadFile";
import { useFormikContext } from "formik";
import { helpAnchor } from "help/anchor";
import { useTaxonomyOptions } from "hooks/useTaxonomyOptions";
import { ETaxonomy, IBenefitPackage } from "library/types";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { CancellationFields } from "./CancellationFields";
import { EligibleFields } from "./EligibleFields";
import { OffboardingFields } from "./OffboardingFields";
import { PriceFields } from "./PriceFields";
import { useCanAddEditPhoto } from "hooks/useCanAddEditPhoto";

type Props = {
  packageData?: IBenefitPackage;
  consentsNeeded?: boolean;
};

export function PackageFormItems({ packageData, consentsNeeded }: Props) {
  const { t } = useTranslation();
  const paymentTypeOptions = useTaxonomyOptions(ETaxonomy.PaymentType);
  const canAddEditPhoto = useCanAddEditPhoto();
  const { values, setFieldValue, errors } = useFormikContext<FormData>();

  const isAutoEnrollment = values["isAutoEnrollment"] as boolean;

  useEffect(() => {
    if (consentsNeeded) return;
    if (isAutoEnrollment) {
      setFieldValue("requiresEnrollmentApproval", false);
    }
  }, [isAutoEnrollment, consentsNeeded]);

  return (
    <Container>
      <Grid $gridColumns={4}>
        <InputField
          data-help="package-dialog-name"
          required
          label={t("greco.name")}
          name="name"
          value={values["name"]}
          setFieldValue={setFieldValue}
          error={errors["name"]}
        />
        <InputField
          data-help="package-dialog-carrier"
          required
          label={t("hbh.carrier.label")}
          name="carrierName"
          value={values["carrierName"]}
          setFieldValue={setFieldValue}
          error={errors["carrierName"]}
        />
        <PriceFields />
        <SelectField
          data-help="package-dialog-payment-type"
          required
          error={errors["paymentType"]}
          showEmptyOption
          setFieldValue={setFieldValue}
          value={values["paymentType"]}
          label={t("hbh.paymentType.label")}
          options={paymentTypeOptions}
          name={"paymentType"}
        />
        <UploadFile
          disabled={!canAddEditPhoto}
          isUploadAppImages
          key={values["coverPhoto"]?.path}
          setFieldValue={setFieldValue}
          fieldName="coverPhoto"
          defaultValue={values["coverPhoto"]}
          label={canAddEditPhoto ? t("hbh.chooseCoverPicture.label") : t("hbh.coverPicture.label")}

        />
        <InputField
          data-help="package-dialog-age-from"
          label={t("hbh.ageLimitLower.label")}
          name="ageLimitLower"
          type="number"
          value={values["ageLimitLower"]}
          setFieldValue={setFieldValue}
          error={errors["ageLimitLower"] || ""}
        />
        <InputField
          data-help="package-dialog-age-to"
          label={t("hbh.ageLimitUpper.label")}
          name="ageLimitUpper"
          type="number"
          value={values["ageLimitUpper"]}
          setFieldValue={setFieldValue}
          error={errors["ageLimitUpper"] || ""}
        />
      </Grid>
      <Grid $gridColumns={1}>
        <TextareaField
          data-help="package-dialog-description"
          label={t("hbh.description.label")}
          name="description"
          value={values["description"]}
          setFieldValue={setFieldValue}
          error={errors["description"]}
          rows={3}
          resize="vertical"
          required
        />
      </Grid>
      <Divider appearance="subtle" />
      <Grid $gridColumns={3}>
        <EligibleFields />
        <CancellationFields />
        <OffboardingFields />
      </Grid>
      <Divider appearance="subtle" />
      <Grid $gridColumns={4}>
        <CheckboxField
          dataHelp={helpAnchor("package-dialog-auto-enrollment")}
          disabled={consentsNeeded}
          name="isAutoEnrollment"
          required
          value={values["isAutoEnrollment"]}
          setFieldValue={setFieldValue}
          label={t("hbh.isAutoEnrollment.label")}
        />
        <CheckboxField
          dataHelp={helpAnchor("package-dialog-upgradeable")}
          name="isUpgradeable"
          required
          value={values["isUpgradeable"]}
          setFieldValue={setFieldValue}
          label={t("hbh.isUpgradeable.label")}
        />
        <CheckboxField
          dataHelp={helpAnchor("package-dialog-enrollment-approval")}
          disabled={isAutoEnrollment || consentsNeeded}
          name="requiresEnrollmentApproval"
          required
          value={values["requiresEnrollmentApproval"]}
          setFieldValue={setFieldValue}
          label={t("hbh.requiresEnrollmentApproval.label")}
        />
        <CheckboxField
          dataHelp={helpAnchor("package-dialog-cancellation-approval")}
          name="requiresCancellationApproval"
          required
          value={values["requiresCancellationApproval"]}
          setFieldValue={setFieldValue}
          label={t("hbh.requiresCancellationApproval.label")}
        />
      </Grid>
      {!packageData ? (
        <AddAttachments dataHelp={helpAnchor("package-dialog-attachments")} />
      ) : (
        <Divider />
      )}

      {!!packageData ? (
        <>
          <Grid>
            <ManagePackageDocuments
              item={packageData as IPackageRowItem}
              dataHelp={helpAnchor("package-dialog-documents")}
            />
          </Grid>
        </>
      ) : null}
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 20px;
`;
